import { z } from 'zod'
import { centsSchema, idSchema } from './catalog.js'

// Lot 3 du back-office : la page « Offres & Abonnements ». Cinq agrégats, un
// par rubrique de l'administration, stockés dans autant d'entrées de
// `site_settings` — pas un document unique : chaque écran écrit sa propre clé
// et deux rubriques éditées à la suite ne peuvent pas s'écraser.
//
// Deux conventions du catalogue s'appliquent ici et primaient sur la forme
// esquissée par la maquette :
//
// 1. **Les montants sont des entiers de centimes** (`centsSchema`), jamais des
//    flottants d'euros. C'est la raison d'être de ce choix, écrite dès le lot
//    2 : « pour que les remises calculées du lot 3 tombent juste ». Un prix
//    promotionnel se calcule ici, donc c'est précisément le lot visé.
// 2. **L'ordre est porté par la position dans le tableau**, aucun champ
//    `ordre`/`position` n'apparaît dans les contrats — comme les familles,
//    groupes et prestations. Deux sources d'ordre finiraient par diverger.

/**
 * Date civile ISO (« 2026-08-31 »), sans heure ni fuseau. Une période
 * d'affichage est une affaire de jours de calendrier, pas d'instants : la
 * gérante saisit « jusqu'au 31 août », pas « jusqu'au 31 août 23 h 59 min 59 s
 * heure de Paris ». Ce format se compare aussi comme du texte, ce dont
 * `seasonState` tire parti pour n'avoir aucun piège de fuseau horaire.
 */
export const isoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'La date doit être au format AAAA-MM-JJ')
  // Contrôle par aller-retour, et non `!Number.isNaN(new Date(...))` : JavaScript
  // ne rejette pas une date civile inexistante, il la fait rouler — « 2026-02-30 »
  // devient le 2 mars sans le moindre signal. Seule la comparaison des
  // composants d'origine à ceux de la date reconstruite l'attrape.
  .refine((value) => {
    const [annee, mois, jour] = value.split('-').map(Number)
    const date = new Date(Date.UTC(annee, mois - 1, jour))
    return (
      date.getUTCFullYear() === annee && date.getUTCMonth() === mois - 1 && date.getUTCDate() === jour
    )
  }, 'Cette date n’existe pas')

/**
 * Le patron commun des blocs saisonniers — promotions, jeux, bandeau
 * d'annonce. Un bloc s'affiche si l'interrupteur est armé **et** si le jour
 * courant tombe dans la période, bornes incluses. Rien d'autre : l'état
 * (« En ligne », « Programmé », « Terminé », « Désarmé ») se calcule au rendu
 * et ne se stocke jamais — le stocker le figerait au dernier enregistrement,
 * et une promotion préparée en août n'apparaîtrait pas d'elle-même le
 * 1ᵉʳ septembre.
 */
export const seasonSchema = z
  .object({
    armed: z.boolean(),
    start: isoDateSchema,
    end: isoDateSchema,
  })
  .refine((season) => season.start <= season.end, {
    path: ['end'],
    message: 'La date de fin ne peut pas précéder la date de début',
  })

export type Season = z.infer<typeof seasonSchema>

const titleSchema = z.string().trim().min(1, 'Le titre est obligatoire')

// Ligne de liste ordonnée (avantage d'un pass, lot d'une roue, étape de
// participation). Une ligne vide n'est jamais un contenu : elle produirait une
// puce fantôme sur le site, comme les `Placeholder` que ce chantier remplace.
const ligneNonVide = (quoi: string) => z.string().trim().min(1, `${quoi} ne peut pas être vide`)

// ─────────────────────────────── Pass ───────────────────────────────

export const passSchema = z.object({
  id: idSchema,
  title: titleSchema,
  durationMonths: z
    .number()
    .int('La durée doit être un nombre entier de mois')
    .min(1, 'La durée doit valoir au moins un mois'),
  priceCents: centsSchema,
  benefits: z.array(ligneNonVide('Un avantage')),
  /** Texte court affiché en pastille sur la carte ; vide = aucune pastille. */
  badge: z.string().trim(),
  /**
   * Le pass que le sélecteur de durée de la page publique montre en premier.
   * Un seul pass peut l'être : l'invariant est tenu par l'API (voir
   * `withSinglePreselected`), pas par ce schéma, parce qu'il porte sur la
   * collection entière et non sur un pass isolé.
   */
  preselected: z.boolean(),
  visible: z.boolean(),
})

export const passesSchema = z.array(passSchema)
export type Pass = z.infer<typeof passSchema>

// ───────────────────────────── Promotions ─────────────────────────────

export const roundingSchema = z.enum(['centime', 'euro'], {
  errorMap: () => ({ message: 'L’arrondi doit être « centime » ou « euro »' }),
})
export type Rounding = z.infer<typeof roundingSchema>

/**
 * Les montants d'un tarif que la remise touche.
 *
 * - `tout` : chaque montant du tarif baisse du même pourcentage. C'est le
 *   comportement d'origine des promotions.
 * - `forfait` : sur un tarif « Séance à l'unité + forfait remisé », seul le
 *   prix du forfait baisse ; la séance à l'unité garde son prix. C'est le
 *   besoin des gérantes pour la lumière pulsée : −20 % sur les forfaits de
 *   8 séances, pas sur la séance.
 */
export const promotionAppliesToSchema = z.enum(['tout', 'forfait'], {
  errorMap: () => ({ message: 'La remise porte sur « tout » le tarif ou sur le « forfait » seulement' }),
})
export type PromotionAppliesTo = z.infer<typeof promotionAppliesToSchema>

export const promotionSchema = z.object({
  id: idSchema,
  title: titleSchema,
  intro: z.string().trim(),
  discountPercent: z
    .number()
    .int('La remise doit être un nombre entier de pourcents')
    .min(1, 'La remise doit valoir au moins 1 %')
    .max(90, 'La remise ne peut pas dépasser 90 %'),
  rounding: roundingSchema,
  /**
   * `forfait` ne vaut que pour des prestations de la situation « Séance à
   * l'unité + forfait remisé ». L'API le vérifie contre le catalogue réel
   * (voir `assertPromotionValide`), pas ici : ce schéma ne connaît que des
   * identifiants.
   *
   * `.default('tout')` plutôt qu'un champ obligatoire : les promotions écrites
   * avant lui (lignes en base, copie de secours embarquée, administration pas
   * encore rechargée) doivent continuer à valider, et garder leur
   * comportement d'origine. Même intention que `keywordsSchema` au catalogue.
   */
  appliesTo: promotionAppliesToSchema.default('tout'),
  season: seasonSchema,
  /**
   * Le compte à rebours de la page publique. Il vise la **fin de période de la
   * promotion** et n'a pas de date propre : une seule date, jamais saisie deux
   * fois — c'est ce qui garantit que le compteur ne survit pas à la promotion
   * qu'il annonce.
   */
  showCountdown: z.boolean(),
  countdownTagline: z.string().trim(),
  /**
   * Prestations du catalogue auxquelles la remise s'applique. Aucun prix n'est
   * copié ici : le tableau de la page se calcule depuis le catalogue, ce qui
   * ferme la divergence constatée au lot 2 (une promotion annonçait 30 €/35 €
   * pour une prestation que le catalogue tarifait 35 €/40 €).
   *
   * Toutes doivent partager la même situation tarifaire — contrainte vérifiée
   * par l'API contre le catalogue réel (voir `assertHomogeneousPricing`), pas
   * ici : ce schéma ne connaît que des identifiants.
   */
  serviceIds: z.array(idSchema),
})

export const promotionsSchema = z.array(promotionSchema)
export type Promotion = z.infer<typeof promotionSchema>

// ─────────────────────────────── Jeux ───────────────────────────────

/**
 * Configuration propre à un type de jeu, isolée derrière un discriminant —
 * même forme que `amounts` pour une prestation. L'institut lancera d'autres
 * offres-jeux ponctuelles : en ajouter un type, c'est ajouter un membre à
 * cette union, sans toucher aux champs communs ni à la table.
 */
const wheelConfigSchema = z.object({
  kind: z.literal('roue'),
  /**
   * Les parts de la roue. Deux au minimum pour qu'il y ait un tirage ; la
   * fourchette lisible (4 à 8) est une indication de l'interface, pas une
   * règle du domaine — une roue à trois lots reste une roue.
   */
  prizes: z.array(ligneNonVide('Un lot')).min(2, 'Une roue compte au moins deux lots'),
  steps: z.array(ligneNonVide('Une étape')),
  /** Légende sous la roue (« Se joue à l’institut »). */
  caption: z.string().trim(),
})

export const gameConfigSchema = z.discriminatedUnion('kind', [wheelConfigSchema])
export type GameConfig = z.infer<typeof gameConfigSchema>
export type GameKind = GameConfig['kind']

export const gameSchema = z.object({
  id: idSchema,
  title: titleSchema,
  /** Accroche (« Jeu estival ») : composée avec les dates dans l'étiquette
   * publique par `gamePeriodLabel`, jamais saisie sous sa forme composée. */
  tagline: z.string().trim(),
  text: z.string().trim(),
  season: seasonSchema,
  legalNotice: z.string().trim(),
  /** Le type d'un jeu est choisi à sa création et n'est plus modifiable
   * ensuite : l'API refuse un changement de `kind` (voir `saveGame`). */
  config: gameConfigSchema,
})

export const gamesSchema = z.array(gameSchema)
export type Game = z.infer<typeof gameSchema>

// ────────────────────────── Bandeau d'annonce ──────────────────────────

/**
 * Cible du lien du bandeau : un bloc de la page, jamais une adresse libre.
 * Une ancre saisie à la main serait cassée au premier renommage, et la gérante
 * n'aurait aucun moyen de s'en apercevoir.
 */
export const bannerTargetSchema = z.enum(['aucun', 'promotion', 'pass', 'jeu', 'cartes-cadeaux'], {
  errorMap: () => ({ message: 'Cette cible de lien n’existe pas' }),
})
export type BannerTarget = z.infer<typeof bannerTargetSchema>

export const bannerSchema = z.object({
  season: seasonSchema,
  text: z.string().trim().min(1, 'Le texte du bandeau est obligatoire'),
  linkLabel: z.string().trim(),
  target: bannerTargetSchema,
})

export type Banner = z.infer<typeof bannerSchema>

// ───────────────────────── Textes de la page ─────────────────────────

// Les arguments de confiance ne sont plus ici : ils sont partagés avec la page
// d'accueil, qui les affichait en dur de son côté. Un seul contenu désormais,
// dans `trustBar.ts` — voir l'en-tête de ce fichier-là.
export const offersTextsSchema = z.object({
  heroTitle: titleSchema,
  heroIntro: z.string().trim(),
  giftTitle: titleSchema,
  giftText: z.string().trim(),
  closingText: z.string().trim(),
})

export type OffersTexts = z.infer<typeof offersTextsSchema>

// ───────────────────── Charge utile de lecture publique ─────────────────────

/**
 * Ce que `GET /api/public/offers` renvoie : les cinq agrégats d'un seul appel.
 * Le site n'a pas à savoir qu'ils vivent dans cinq entrées distinctes, et une
 * page n'a pas à faire cinq requêtes pour s'afficher.
 *
 * La lecture publique ne filtre PAS les blocs hors période : le site reçoit
 * les périodes et applique `isLive` au rendu. C'est ce qui permet à la copie
 * de secours embarquée à la compilation de rester juste — figée, elle
 * afficherait sinon indéfiniment la promotion de l'été où elle a été produite.
 */
export const offersPayloadSchema = z.object({
  passes: passesSchema,
  promotions: promotionsSchema,
  games: gamesSchema,
  banner: bannerSchema,
  texts: offersTextsSchema,
})

export type OffersPayload = z.infer<typeof offersPayloadSchema>

// ──────────────────────────── Valeurs de départ ────────────────────────────

// Reprise fidèle du contenu écrit en dur dans
// src/pages/OffresAbonnement/OffresAbonnement.jsx avant ce lot. Ces valeurs
// servent deux rôles : le seed de la base, et le repli servi par l'API quand
// une clé est absente — c'est pourquoi elles sont ici et non dans un script.
//
// Les identifiants sont fixes, pas tirés au hasard : ils doivent être les
// mêmes en base, dans la copie de secours du site et dans les tests. Un uuid
// aléatoire à chaque démarrage ferait d'un rechargement une migration.

export const SEASON_ETE_2026: Season = { armed: true, start: '2026-07-01', end: '2026-08-31' }

export const DEFAULT_PASSES: Pass[] = [
  {
    id: '5d6f1a4e-0b3c-4f28-9a71-6c2d8e5f0a11',
    title: 'Pass 6 mois',
    durationMonths: 6,
    priceCents: 5000,
    benefits: [
      '50% de remise sur les épilations classiques',
      'Réductions exclusives sur l’ensemble des prestations',
      'Accès prioritaire aux nouveaux rendez-vous',
    ],
    badge: '',
    preselected: false,
    visible: true,
  },
  {
    id: '2f8b9c07-4d51-4e6a-8f30-1b7a5c9d2e44',
    title: 'Pass 12 mois',
    durationMonths: 12,
    priceCents: 8000,
    benefits: [
      '50% de remise sur les épilations classiques',
      'Réductions exclusives sur l’ensemble des prestations',
      'Accès prioritaire aux nouveaux rendez-vous',
      'Carte membre à présenter à chaque visite',
    ],
    badge: '✦ Meilleure offre',
    preselected: true,
    visible: true,
  },
]

/**
 * `serviceIds` est vide ici, et c'est voulu : les identifiants du catalogue
 * sont créés par l'import des 137 prestations et ne peuvent pas être connus
 * d'avance. Le script de seed (`db/seedOffers.ts`) les résout par leur nom
 * contre la base réelle et complète cette liste. Servie telle quelle en repli,
 * la promotion n'affiche donc aucune ligne — c'est le comportement voulu :
 * mieux vaut un tableau vide qu'un prix inventé.
 */
export const DEFAULT_PROMOTIONS: Promotion[] = [
  {
    id: '9e3d5f81-7a2b-4c60-b1e8-3f4a6d8c0b52',
    title: 'Promotion Juillet 2026 · Soins des pieds',
    intro:
      'Vos pieds vous portent toute la journée : offrez-leur une parenthèse de raffinement avec nos soins signature, à prix privilégié.',
    discountPercent: 20,
    rounding: 'euro',
    appliesTo: 'tout',
    season: SEASON_ETE_2026,
    showCountdown: true,
    countdownTagline: '−20 % sur nos soins des pieds, jusqu’au 31 août.',
    serviceIds: [],
  },
]

export const DEFAULT_GAMES: Game[] = [
  {
    id: 'c4a7b210-8e56-4d39-9f02-5b1c7e3a9d68',
    title: 'La Roue Estivale',
    tagline: 'Jeu estival',
    text:
      'Une invitation au jeu, digne de votre visite : chaque passage en caisse fait tourner la roue, et chaque tour révèle un privilège — bon d’achat, remise ou soin offert.',
    season: SEASON_ETE_2026,
    legalNotice: 'Voir règlement complet à l’accueil.',
    config: {
      kind: 'roue',
      prizes: [
        'Bon d’achat 10€',
        '-20% sur un soin',
        'Soin du regard offert',
        'Bon d’achat 5€',
        '-10% sur un soin',
        'Rejouez demain',
      ],
      steps: ['Faites tourner la roue à l’accueil.', 'Repartez avec votre gain immédiat.'],
      caption: 'Se joue à l’institut',
    },
  },
]

export const DEFAULT_BANNER: Banner = {
  season: SEASON_ETE_2026,
  text: 'La Roue Estivale tourne jusqu’au 31 août — un passage en caisse, une chance de gagner.',
  linkLabel: 'Voir les lots',
  target: 'jeu',
}

export const DEFAULT_OFFERS_TEXTS: OffersTexts = {
  heroTitle: 'Des privilèges pensés pour votre bien-être, à l’année.',
  heroIntro:
    'Chaque instant passé chez Azoya Spa mérite d’être récompensé. Découvrez nos pass abonnements et notre promotion du mois, réservés à celles et ceux qui font de la sérénité une habitude.',
  giftTitle: 'Les cartes cadeaux pour faire plaisir',
  giftText:
    'Faites plaisir à vos proches, offrez des moments de détente. Cartes cadeaux valables pour une prestation ou un montant libre.',
  closingText:
    'Offrez-vous une fidélité qui se ressent, jusque dans les moindres détails. Rejoignez le pass 12 mois et laissez la sérénité devenir une habitude.',
}

export const DEFAULT_OFFERS_PAYLOAD: OffersPayload = {
  passes: DEFAULT_PASSES,
  promotions: DEFAULT_PROMOTIONS,
  games: DEFAULT_GAMES,
  banner: DEFAULT_BANNER,
  texts: DEFAULT_OFFERS_TEXTS,
}

// Clés de `site_settings`. Regroupées ici pour que l'API, le seed et les tests
// citent la même chaîne — une faute de frappe sur l'une d'elles produirait un
// écran qui lit sagement une clé que personne n'écrit jamais.
export const OFFERS_KEYS = {
  passes: 'offers:passes',
  promotions: 'offers:promotions',
  games: 'offers:games',
  banner: 'offers:banner',
  texts: 'offers:texts',
} as const
